import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';

import { AccessErrorBody } from '../../models/access-error.model';
import {
  AccessErrorAction,
  InterpretedAccessError,
  interpretAccessError,
} from '../../utils/access-error.utils';
import { NotificationService } from '../notification/notification.service';

/**
 * Central handler for feature-access denial responses.
 *
 * `AccessErrorInterceptor` calls `dispatch(body)` on every 4xx that
 * matches the BE denial contract. This service owns the toast + CTA
 * side of the story: it interprets the body via `interpretAccessError`
 * and — if the interpretation carries a CTA — shows a snack with the
 * labelled button, then routes the user to the appropriate destination
 * when they click it.
 *
 * Why a service (not inline in the interceptor):
 *   - CTA actions need to `Router.navigate(...)`; interceptors are
 *     pure functions and can't cleanly inject Router without extra
 *     ceremony.
 *   - De-duplication (see `_recentlyShown`) needs to persist across
 *     requests; a service holds that state.
 *   - Tests can unit-test the dispatcher without booting the whole
 *     interceptor chain.
 *
 * De-dup rationale: a page that fires 4 parallel calls to a
 * feature-gated endpoint on load would otherwise show 4 identical
 * toasts. We keep a short-lived (2 s) fingerprint of the last-shown
 * error so bursts collapse to one — 2 s is long enough to catch a
 * page-load fan-out, short enough that a genuine re-attempt after
 * the user clicks somewhere else still surfaces a toast.
 */
@Injectable({ providedIn: 'root' })
export class AccessErrorDispatcher {
  private readonly notifications = inject(NotificationService);
  private readonly router = inject(Router);
  private readonly transloco = inject(TranslocoService);

  /** Fingerprint → timestamp of last-shown-at (ms). Only kept for
   *  entries within the de-dup window; older entries are pruned on
   *  every call so this stays bounded. */
  private readonly _recentlyShown = new Map<string, number>();
  private static readonly _DEDUP_WINDOW_MS = 2_000;

  /** Called by the interceptor. Body is the raw JSON error payload
   *  from a 4xx response; the interpreter narrows and enriches it. */
  dispatch(body: AccessErrorBody): void {
    // Fingerprint on the machine fields — same tuple = same UX decision, so
    // collapse repeats. A different feature_id or area (e.g. the user hits
    // two gated endpoints in a row) surfaces both toasts.
    //
    // `code`, not `error`: since 2026-09-08 `error` is a human sentence, and
    // fingerprinting on prose would collapse two genuinely different denials
    // that happen to share wording, and split one denial whose copy the
    // backend later rewords.
    const fingerprint = [
      body.code,
      body.hint ?? '',
      body.feature_id ?? '',
      body.area ?? '',
      body.mode ?? '',
    ].join('|');
    if (this._recentlyShownWithin(fingerprint, AccessErrorDispatcher._DEDUP_WINDOW_MS)) {
      return;
    }
    this._markShown(fingerprint);

    const interpreted = interpretAccessError(body, this.transloco);
    this._showToast(interpreted);
  }

  private _showToast(interpreted: InterpretedAccessError): void {
    // Compose title + message into one line (MatSnackBar is a single-
    // line surface). Title stays terse; message gives context.
    const line = interpreted.title
      ? `${interpreted.title}. ${interpreted.message}`
      : interpreted.message;

    if (!interpreted.cta) {
      // No user action → plain error toast, dismisses on timeout.
      this.notifications.error(line);
      return;
    }

    // Actionable → surface the CTA as the snack's action button.
    const ref = this.notifications.withAction(line, interpreted.cta.label, 'error');
    ref.onAction().subscribe(() => this._runAction(interpreted.cta!.action));
  }

  private _runAction(action: AccessErrorAction): void {
    switch (action) {
      case 'go_to_billing':
        // Sends user to the plan-picker. `/billing` is the existing
        // route the sidebar plan-widget links to.
        this.router.navigate(['/billing'], { queryParams: { tab: 'subscription' } });
        return;

      case 'open_stripe_portal':
        // Land the user on the billing page — the Stripe portal button
        // lives there. We don't mint a portal session directly from
        // the dispatcher because that would duplicate the "how do we
        // open the portal" decision (already lives in the billing
        // route). A future refinement can auto-open the portal on a
        // query-param handshake; today one click from `/billing` is
        // fine.
        this.router.navigate(['/billing'], { queryParams: { tab: 'subscription' } });
        return;

      case 'contact_platform_admin':
        // No modal component ships in this PR — send the user to the
        // customer support page which surfaces the admin contact.
        // Follow-up ticket can wire a proper contact modal if the
        // support page proves too indirect.
        this.router.navigate(['/organisation-support']);
        return;

      case 'request_access':
        // Same destination as `contact_platform_admin` today, deliberately a
        // separate action: the two are different asks ("grant me this area"
        // versus "un-suspend what you already granted"), the button labels
        // differ accordingly, and keeping them apart means a future request-
        // access flow can be wired here without touching the grant-lifecycle
        // path.
        this.router.navigate(['/organisation-support']);
        return;

      case 'switch_org':
        // The dashboard is where the org switcher lives, and it is safe
        // for every caller — including a delegate whose grant was just
        // revoked, who lands on the empty-workspace state there rather
        // than on another denial.
        this.router.navigate(['/dashboard']);
        return;

      case 'retry':
        // Interceptors can't cleanly re-fire the original request from
        // here (the observable chain has already errored). The retry
        // CTA exists to let the user acknowledge and take their own
        // next step (typically clicking the same button in the app).
        // Documented as such in `access-error.utils` — this branch is
        // intentionally a no-op.
        return;
    }
  }

  private _recentlyShownWithin(fingerprint: string, windowMs: number): boolean {
    this._prune(windowMs);
    const last = this._recentlyShown.get(fingerprint);
    if (last === undefined) return false;
    return Date.now() - last < windowMs;
  }

  private _markShown(fingerprint: string): void {
    this._recentlyShown.set(fingerprint, Date.now());
  }

  /** Drop entries older than `windowMs`. Called on every dispatch so
   *  the map never grows unboundedly under a bad-actor 4xx flood. */
  private _prune(windowMs: number): void {
    const cutoff = Date.now() - windowMs;
    for (const [key, ts] of this._recentlyShown) {
      if (ts < cutoff) this._recentlyShown.delete(key);
    }
  }
}
